import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Search, RefreshCw, Plus, ChevronLeft, ChevronRight, Users, UserCheck, Clock, UserX } from 'lucide-react';
import { patientsApi } from '@/api/endpoints/patients.api';
import { careLinesApi } from '@/api/endpoints/carelines.api';
import type { RiskLevel, PatientProgramStatus } from '@/types/patient.types';
import { ProgramStatusLabels } from '@/types/patient.types';

export default function PatientsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Pagination
  const [page, setPage] = useState(0);
  const pageSize = 10;

  // Filters from URL
  const searchFromUrl = searchParams.get('search') || '';
  const riskLevelFromUrl = searchParams.get('riskLevel') || '';
  const careLineIdFromUrl = searchParams.get('careLineId') || '';
  const programStatusFromUrl = searchParams.get('programStatus') || '';

  // Local filter state
  const [searchTerm, setSearchTerm] = useState(searchFromUrl);
  const [riskLevel, setRiskLevel] = useState<string>(riskLevelFromUrl);
  const [careLineId, setCareLineId] = useState(careLineIdFromUrl);
  const [programStatus, setProgramStatus] = useState<string>(programStatusFromUrl);

  // Debounced search using useMemo
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);

  // Debounce effect
  useMemo(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Update URL when filters change
  useMemo(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (riskLevel) params.set('riskLevel', riskLevel);
    if (careLineId) params.set('careLineId', careLineId);
    if (programStatus) params.set('programStatus', programStatus);
    setSearchParams(params, { replace: true });
    setPage(0);
  }, [debouncedSearch, riskLevel, careLineId, programStatus, setSearchParams]);

  // Query for care lines (cached)
  const { data: careLines = [] } = useQuery({
    queryKey: ['care-lines', 'active'],
    queryFn: () => careLinesApi.getAllActive(),
    staleTime: 1000 * 60 * 10, // 10 minutes - rarely changes
  });

  // Query for status counts (cached)
  const { data: statusCounts = { total: 0, eligible: 0, captation: 0, active: 0, inactive: 0, discharged: 0 } } = useQuery({
    queryKey: ['patients', 'status-counts'],
    queryFn: () => patientsApi.getStatusCounts(),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  // Main patients query with filters
  const {
    data: patientsData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['patients', { page, size: pageSize, search: debouncedSearch, riskLevel, careLineId, programStatus }],
    queryFn: () => patientsApi.getAll({
      page,
      size: pageSize,
      search: debouncedSearch || undefined,
      riskLevel: (riskLevel as RiskLevel) || undefined,
      careLineId: careLineId || undefined,
      programStatus: (programStatus as PatientProgramStatus) || undefined,
    }),
    staleTime: 1000 * 60 * 2, // 2 minutes
    placeholderData: keepPreviousData, // Keep showing old data while loading new
  });

  const patients = patientsData?.content || [];
  const totalPages = patientsData?.totalPages || 0;
  const totalElements = patientsData?.totalElements || 0;

  const getRiskBadge = (level?: RiskLevel) => {
    const styles = {
      LOW: 'bg-green-100 text-green-800',
      MODERATE: 'bg-yellow-100 text-yellow-800',
      HIGH: 'bg-red-100 text-red-800',
      CRITICAL: 'bg-purple-100 text-purple-800',
    };
    const labels = {
      LOW: 'Baixo',
      MODERATE: 'Moderado',
      HIGH: 'Alto',
      CRITICAL: 'Crítico',
    };

    if (!level) {
      return (
        <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
          Não avaliado
        </span>
      );
    }

    return (
      <span className={`rounded-full px-2 py-1 text-xs font-medium ${styles[level]}`}>
        {labels[level]}
      </span>
    );
  };

  const getStatusBadge = (status?: PatientProgramStatus) => {
    const styles: Record<PatientProgramStatus, string> = {
      ELIGIBLE: 'bg-blue-100 text-blue-800',
      CAPTATION: 'bg-yellow-100 text-yellow-800',
      ACTIVE: 'bg-green-100 text-green-800',
      INACTIVE: 'bg-gray-100 text-gray-800',
      DISCHARGED: 'bg-purple-100 text-purple-800',
    };

    if (!status) {
      return (
        <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
          -
        </span>
      );
    }

    return (
      <span className={`rounded-full px-2 py-1 text-xs font-medium ${styles[status]}`}>
        {ProgramStatusLabels[status]}
      </span>
    );
  };

  const formatCpf = (cpf: string) => {
    if (cpf.includes('*')) return cpf;
    return `${cpf.slice(0, 3)}.***.***-${cpf.slice(9)}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Pacientes</h1>
          <p className="text-muted-foreground">
            {totalElements > 0 ? `${totalElements} paciente${totalElements > 1 ? 's' : ''} encontrado${totalElements > 1 ? 's' : ''}` : 'Gerenciar carteira de pacientes'}
          </p>
        </div>
        <button
          onClick={() => navigate('/patients/new')}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Novo Paciente
        </button>
      </div>

      {/* Status Summary Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <button
          onClick={() => { setProgramStatus(''); }}
          className={`rounded-lg border p-4 text-left transition-colors hover:bg-muted/50 ${!programStatus ? 'border-primary bg-primary/5' : ''}`}
        >
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Total</span>
          </div>
          <p className="mt-2 text-2xl font-bold">{statusCounts.total}</p>
        </button>

        <button
          onClick={() => { setProgramStatus('ELIGIBLE'); }}
          className={`rounded-lg border p-4 text-left transition-colors hover:bg-muted/50 ${programStatus === 'ELIGIBLE' ? 'border-blue-500 bg-blue-50' : ''}`}
        >
          <div className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-600">Elegíveis</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-blue-600">{statusCounts.eligible}</p>
        </button>

        <button
          onClick={() => { setProgramStatus('ACTIVE'); }}
          className={`rounded-lg border p-4 text-left transition-colors hover:bg-muted/50 ${programStatus === 'ACTIVE' ? 'border-green-500 bg-green-50' : ''}`}
        >
          <div className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-green-600" />
            <span className="text-sm font-medium text-green-600">Ativos</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-green-600">{statusCounts.active}</p>
        </button>

        <button
          onClick={() => { setProgramStatus('CAPTATION'); }}
          className={`rounded-lg border p-4 text-left transition-colors hover:bg-muted/50 ${programStatus === 'CAPTATION' ? 'border-yellow-500 bg-yellow-50' : ''}`}
        >
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-600">Em Captação</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-yellow-600">{statusCounts.captation}</p>
        </button>

        <button
          onClick={() => { setProgramStatus('INACTIVE'); }}
          className={`rounded-lg border p-4 text-left transition-colors hover:bg-muted/50 ${programStatus === 'INACTIVE' ? 'border-gray-500 bg-gray-50' : ''}`}
        >
          <div className="flex items-center gap-2">
            <UserX className="h-5 w-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-600">Inativos</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-gray-600">{statusCounts.inactive}</p>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por nome ou CPF..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border bg-background pl-10 pr-4 py-2"
          />
        </div>
        <select
          value={riskLevel}
          onChange={(e) => setRiskLevel(e.target.value)}
          className="rounded-lg border bg-background px-4 py-2"
        >
          <option value="">Todos os riscos</option>
          <option value="LOW">Baixo</option>
          <option value="MODERATE">Moderado</option>
          <option value="HIGH">Alto</option>
          <option value="CRITICAL">Crítico</option>
          <option value="HIGH,CRITICAL">Alto + Crítico</option>
        </select>
        <select
          value={careLineId}
          onChange={(e) => setCareLineId(e.target.value)}
          className="rounded-lg border bg-background px-4 py-2"
        >
          <option value="">Todas as linhas</option>
          {careLines.map((cl) => (
            <option key={cl.id} value={cl.id}>
              {cl.name}
            </option>
          ))}
        </select>
        <select
          value={programStatus}
          onChange={(e) => setProgramStatus(e.target.value)}
          className="rounded-lg border bg-background px-4 py-2"
        >
          <option value="">Todos os status</option>
          <option value="ELIGIBLE">Elegível</option>
          <option value="CAPTATION">Em Captação</option>
          <option value="ACTIVE">Ativo</option>
          <option value="INACTIVE">Inativo</option>
          <option value="DISCHARGED">Desligado (Alta)</option>
        </select>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 rounded-lg border px-4 py-2 hover:bg-muted"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-red-800">Erro ao carregar pacientes. Tente novamente.</p>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border bg-card">
        {isLoading && patients.length === 0 ? (
          <div className="flex h-64 items-center justify-center">
            <RefreshCw className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Carregando...</span>
          </div>
        ) : patients.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-muted-foreground">
            <p>Nenhum paciente encontrado</p>
            {(searchTerm || riskLevel || careLineId || programStatus) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setRiskLevel('');
                  setCareLineId('');
                  setProgramStatus('');
                }}
                className="mt-2 text-primary hover:underline"
              >
                Limpar filtros
              </button>
            )}
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Nome</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">CPF</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Idade</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Risco</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Problemas</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Ações</th>
              </tr>
            </thead>
            <tbody>
              {patients.map((patient) => (
                <tr
                  key={patient.id}
                  className="border-b transition-colors hover:bg-muted/50"
                >
                  <td className="px-4 py-3 font-medium">{patient.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{formatCpf(patient.cpf)}</td>
                  <td className="px-4 py-3">{patient.age} anos</td>
                  <td className="px-4 py-3">{getStatusBadge(patient.programStatus)}</td>
                  <td className="px-4 py-3">{getRiskBadge(patient.riskLevel)}</td>
                  <td className="px-4 py-3">{patient.activeProblemsCount} ativo{patient.activeProblemsCount !== 1 ? 's' : ''}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/patients/${patient.id}`)}
                      className="text-sm text-primary hover:underline"
                    >
                      Ver detalhes
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-4 py-3">
            <p className="text-sm text-muted-foreground">
              Página {page + 1} de {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="flex items-center gap-1 rounded border px-3 py-1 text-sm disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="flex items-center gap-1 rounded border px-3 py-1 text-sm disabled:opacity-50"
              >
                Próxima
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
